import React, {useEffect, useRef, useState, useMemo} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import styles from './AggregateResequenceTable.module.css';
import {AutoSizer, List} from 'react-virtualized';
import {Popover} from '@material-ui/core';
import {columnsPropType} from '../../types';
import {TableRow, TableHead} from '../../atomicComponents';

const AggregateResequenceTable = ({
  listId,
  rows: data,
  columns: originalColumns,
  classNames = {},
  rowHeight = 38,
  headerHeight,
  defaultColumns: originalDefaultColumns,
  primaryAction,
  secondaryActions = [],
  setHasActiveEdits,
  setFieldToFiltersSetMap,
  fieldToFiltersSetMap,
  setSelectedColumns,
  renderPlaceholder,
  handleRowSelection,
  handleAllRowsSelect,
}) => {
  const [rowsData, setRowsData] = useState({
    data: [...data],
    filteredData: [...data],
  });

  const [fieldToSearchTextMap, setFieldToSearchTextMap] = useState({});
  const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const [ellipsisPopupValue, setEllipsisPopupValue] = useState(null);
  // const [fieldToFiltersSetMap, setFieldToFiltersSetMap] = useState({});

  const closeEllipsisPopup = () => setIsEllipsisPopupOpen(false);

  const vListRef = useRef(null);
  const tableHeadRef = useRef(null);

  const handleListScroll = e =>
    (tableHeadRef.current.scrollLeft = e.target.scrollLeft);

  const syncListToTableHead = e =>
    (vListRef.current.scrollLeft = e.target.scrollLeft);

  useEffect(() => {
    vListRef.current = document.getElementById(listId);

    if (vListRef.current) {
      vListRef.current.addEventListener('scroll', handleListScroll);
      tableHeadRef.current.addEventListener('scroll', syncListToTableHead);
    }
  }, [listId, rowsData.data.length]);

  const handlePopoverOpen = (event, val) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setEllipsisPopupValue(val);
      setIsEllipsisPopupOpen(true);
      setEllipsisAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setIsEllipsisPopupOpen(false);
    setEllipsisAnchorEl(null);
  };

  useEffect(() => {
    if (setHasActiveEdits) {
      const areEditsActive = rowsData.data.some(row => row.isSelected);
      setHasActiveEdits(areEditsActive);
    }
  }, [rowsData.data, setHasActiveEdits]);

  const defaultColumns = useMemo(
    () =>
      [
        {
          title: 'select',
          field: '_select',
          width: 30,
          renderHeader: rows => {
            return (
              <input
                type="checkbox"
                onChange={() =>
                  handleAllRowsSelect(new Set(rows.map(row => row.id)))
                }
                checked={rows?.length && rows.every(row => row.isSelected)}
              />
            );
          },
          render: ({isSelected, id}) => (
            <input
              style={{marginTop: 0, marginBottom: 0}}
              type="checkbox"
              onChange={() => handleRowSelection(id)}
              checked={!!isSelected}
              data-testid={`select-row-${id}`}
              id={`select-row-${id}`}
              name={`select-row-${id}`}
            />
          ),
        },
        {
          title: '#',
          field: '_serialNumber',
          width: 54,
          render: (_, idx) => idx,
          renderHeader: rows => (
            <div className={styles.asnsCount}>{rows?.length || 0}</div>
          ),
          headerStyles: {
            paddingLeft: 7,
            paddingRight: 2,
          },
        },

        ...originalDefaultColumns,
      ].filter(i => i),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [originalDefaultColumns],
  );
  const noOfFixedColumns = primaryAction || secondaryActions?.length ? 2 : 1;

  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const reorderHeaders = result => {
    // dropped outside the list or dropped at serial number place
    if (!result.destination || result.destination.index < noOfFixedColumns) {
      return;
    }

    const reorderedColumns = reorder(
      originalDefaultColumns,
      result.source.index,
      result.destination.index,
    );
    // setSelectedColumns(reorderedColumns);
  };

  const rowRenderer = ({style, index, key}) => {
    const handleCopy = text => {
      navigator.clipboard.writeText(text);
      setShowCopyAlert(true);
    };
    return (
      <TableRow
        index={index}
        style={style}
        key={key}
        row={rowsData.filteredData[index]}
        columns={defaultColumns}
        classNames={{...classNames}}
        rowHeight={rowHeight}
        onCopy={handleCopy}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
      />
    );
  };

  useEffect(() => {
    if (showCopyAlert) setShowCopyAlert(false);
  }, [showCopyAlert]);

  useDeepCompareColumnArraysEffect(() => {
    setSelectedColumns(originalDefaultColumns);
  }, [originalDefaultColumns]);

  useEffect(() => {
    setRowsData(prevState => {
      const dataFilteredBySearchTexts = filterBySearchTexts(
        fieldToSearchTextMap,
        prevState.data,
        originalDefaultColumns,
      );

      const dataFilteredBySearchAndFilters = filterBySelectedFilters(
        fieldToFiltersSetMap,
        dataFilteredBySearchTexts,
        originalDefaultColumns,
      );

      return {...prevState, filteredData: dataFilteredBySearchAndFilters};
    });
  }, [
    originalDefaultColumns,
    fieldToFiltersSetMap,
    fieldToSearchTextMap,
    rowsData.data,
  ]);

  useEffect(() => {
    setRowsData({
      data: [...data],
      filteredData: [...data],
    });
  }, [data]);

  return (
    <>
      <div
        style={{width: '100%', height: '100%'}}
        className={clsx(
          styles.baseContainer,
          classNames.tableContainerClassName,
        )}
      >
        <div
          style={{width: '100%'}}
          className={clsx(
            styles.basicTableContainer,
            classNames.tableContainerClassName,
          )}
        >
          <AutoSizer>
            {({width: windowWidth, height: windowHeight}) => {
              let width = windowWidth;
              let height = windowHeight;
              const listHeight = height - rowHeight - 2;
              return (
                <div
                  className={clsx(styles.basicTable, classNames.tableClassName)}
                  style={{
                    height,
                    width,
                  }}
                >
                  {!rowsData.data.length && renderPlaceholder ? (
                    renderPlaceholder()
                  ) : (
                    <>
                      <TableHead
                        columns={defaultColumns}
                        ref={tableHeadRef}
                        className={classNames.tableHeadClassName}
                        height={headerHeight || rowHeight}
                        reorderHeaders={reorderHeaders}
                        rows={rowsData.filteredData}
                        allRows={rowsData.data}
                        fieldToSearchTextMap={fieldToSearchTextMap}
                        setSearchTextForTitleCurry={field => value =>
                          setFieldToSearchTextMap(prev => ({
                            ...prev,
                            [field]: value.toLowerCase(),
                          }))}
                        noOfFixedColumns={noOfFixedColumns}
                        fieldToFiltersSetMap={fieldToFiltersSetMap}
                        setFiltersForFieldCurry={field => selectedFilters =>
                          setFieldToFiltersSetMap(prev => ({
                            ...prev,
                            [field]: new Set(selectedFilters),
                          }))}
                      />
                      <List
                        id={listId}
                        ref={vListRef}
                        containerStyle={{overflow: 'initial'}}
                        rowRenderer={rowRenderer}
                        height={listHeight}
                        rowHeight={rowHeight}
                        rowCount={rowsData.filteredData.length}
                        width={width}
                        className={clsx(
                          styles.scrollBar,
                          styles.heightAdjustedListScroll,
                        )}
                        overscanRowCount={10}
                      />
                    </>
                  )}
                </div>
              );
            }}
          </AutoSizer>
        </div>
      </div>
      <Popover
        open={isEllipsisPopupOpen}
        anchorEl={ellipsisAnchorEl}
        onClose={closeEllipsisPopup}
        className={styles.popover}
        classes={{
          paper: styles.popoverPaper,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {ellipsisPopupValue}
      </Popover>
      <div
        className={clsx(
          styles.copyAlert,
          showCopyAlert ? styles.visible : styles.hidden,
        )}
      >
        Text copied
      </div>
    </>
  );
};

AggregateResequenceTable.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    tableHeadClassName: PropTypes.string,
  }),
  row: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any.isRequired,
    }),
  ),
  columns: columnsPropType,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  defaultColumns: columnsPropType,
  primaryAction: PropTypes.shape({
    name: PropTypes.string.isRequired,
    actionFn: PropTypes.func.isRequired,
  }),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      actionFn: PropTypes.func.isRequired,
      backgroundColor: PropTypes.string,
    }),
  ),
  setHasActiveEdits: PropTypes.func,
  renderPlaceholder: PropTypes.func,
};

export default AggregateResequenceTable;

function filterBySearchTexts(fieldToSearchTextMap, data, columns) {
  const fieldToSearchFnMap = columns.reduce((acc, col) => {
    if (col.searchFn) return {...acc, [col.field]: col.searchFn};
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToSearchTextMap).forEach(entry => {
    const [field, searchText] = entry;
    const defaultSearchFn = (fieldValue, searchValue) =>
      fieldValue.toLowerCase().startsWith(searchValue);

    const searchFn = fieldToSearchFnMap[field] || defaultSearchFn;

    filteredData = filteredData.filter(
      row => !searchText || !row[field] || searchFn(row[field], searchText),
    );
  });
  return filteredData;
}

function filterBySelectedFilters(fieldToFiltersSetMap, data, columns) {
  const fieldToFilterFnMap = columns.reduce((acc, col) => {
    if (col.filterFn) return {...acc, [col.field]: col.filterFn};
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToFiltersSetMap).forEach((entry, idx) => {
    const [field, filtersSet] = entry;
    const filterFn = fieldToFilterFnMap[field];

    filteredData = filteredData.filter(row => {
      if (!filterFn) return !filtersSet?.size || filtersSet.has(row[field]);
      if (!idx) {
        console.log(' [...filtersSet]', [...filtersSet]);
      }

      return (
        !filtersSet?.size ||
        [...filtersSet].some(filter => filterFn(row, filter.value))
      );
    });
  });
  return filteredData;
}

function useDeepCompareColumnArraysEffect(callback, dependencies) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareColumnArraysMemoize));
}

function useDeepCompareColumnArraysMemoize(value) {
  const ref = useRef();
  if (!deepCompareEqualsColumnsArray(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function deepCompareEqualsColumnsArray(oldArray, newArray) {
  if (oldArray?.length !== newArray?.length) return false;
  const zippedArray = oldArray.map((e, i) => [e, newArray[i]]);
  return zippedArray.every(tuple => tuple[0].title === tuple[1].title);
}
